import { AddressElement, Elements } from "@stripe/react-stripe-js"
import classNames from "classnames"
import format from "date-fns/format"
import PropTypes from "prop-types"
import React, { Fragment, useMemo, useState } from "react"
import { useMutation } from "react-query"
import { useDockwaPlusContext } from "src/main/Account/Settings/DockwaPlusContext"
import { DockwaPlusMembershipCardSmall } from "src/main/Account/Settings/DockwaPlusMembershipCard"
import Stanwood from "src/main/Account/Settings/Stanwood"

import Form from "src/components/Form"
import LineItems from "src/components/LineItems/LineItems"
import Modal from "src/components/Modal"
import SelectPaymentMethod from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import { createDockwaPlusMembership } from "src/api/Account/Settings"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { stripePromise } from "src/utils/StripePromise"

const ACCEPTED_PAYMENT_METHODS = ["card"]

const DockwaPlusMembershipSignupModal = ({
  isOpen,
  setIsOpen,
  setUser,
  paymentMethods,
  setPaymentMethods,
  onPurchaseSuccess,
  size = "mediumResponsive",
}) => {
  const {
    address,
    setAddress,
    addressComplete,
    setAddressComplete,
    couponCode,
    updateField,
    resetForm,
    productInfo: {
      data: dockwaPlusProductInformation,
      isLoading: productInformationLoading,
    },
  } = useDockwaPlusContext()
  const tracker = useTracker()
  const [paymentMethod, setPaymentMethod] = useState("")
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] =
    useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [purchasedUserData, setPurchasedUserData] = useState(null)
  const [showCouponInput, setShowCouponInput] = useState(false)

  const creditCards = useMemo(() => {
    if (!paymentMethods) return []
    return paymentMethods.filter(
      (method) =>
        method.type === "PaymentMethod::Card" && method.deleted !== true
    )
  }, [paymentMethods])

  const showToast = useToast()

  // Get existing billing address from selected payment method to pre-fill AddressElement
  const existingBillingAddress = useMemo(() => {
    if (!paymentMethod || paymentMethod === "add") return undefined

    const method = creditCards.find(
      (m) => m.stripePaymentMethodId === paymentMethod
    )

    if (!method?.billingDetails?.address) return undefined

    const addr = method.billingDetails.address
    return {
      name: method.billingDetails.name || "",
      address: {
        line1: addr.line1 || "",
        line2: addr.line2 || "",
        city: addr.city || "",
        state: addr.state || "",
        postal_code: addr.postalCode || "",
        country: addr.country || "US",
      },
    }
  }, [creditCards, paymentMethod])

  const { mutate: createMembership, isLoading: creatingMembership } =
    useMutation((data) => createDockwaPlusMembership({ data }), {
      onSuccess: (data) => {
        // Store data for confirmation screen - DON'T call setUser or
        // onPurchaseSuccess yet! These are deferred until the user clicks
        // "Continue" on the confirmation screen. This ensures the user sees
        // the confirmation before any parent re-renders occur.
        setPurchasedUserData(data)
        setShowConfirmation(true)

        const { dockwa_plus_external_id: dockwaPlusExternalId } =
          dockwaPlusProductInformation

        tracker.trackEvent("dockwa_plus:membership_purchased", {
          dockwa_plus_external_id: dockwaPlusExternalId,
        })
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })

        const { dockwa_plus_external_id: dockwaPlusExternalId } =
          dockwaPlusProductInformation

        tracker.trackEvent("dockwa_plus:membership_purchase_failed", {
          dockwa_plus_external_id: dockwaPlusExternalId,
          error: error.message,
        })
      },
    })

  const submitButtonText = () => {
    if (creatingMembership) {
      return "Purchasing"
    } else {
      return "Purchase Dockwa+"
    }
  }

  const handleClose = ({ onSuccess } = { onSuccess: false }) => {
    if (!onSuccess && dockwaPlusProductInformation?.dockwa_plus_external_id) {
      const { dockwa_plus_external_id: dockwaPlusExternalId } =
        dockwaPlusProductInformation

      tracker.trackEvent("dockwa_plus:cancel_purchase_pressed", {
        dockwa_plus_external_id: dockwaPlusExternalId,
      })
    }

    setIsOpen(false)
    resetForm()
    setPaymentMethod("")
    setShowCouponInput(false)
    setShowConfirmation(false)
    setPurchasedUserData(null)
  }

  const handleContinue = () => {
    const { dockwa_plus_external_id: dockwaPlusExternalId } =
      dockwaPlusProductInformation

    tracker.trackEvent("dockwa_plus:continue_pressed", {
      dockwa_plus_external_id: dockwaPlusExternalId,
    })

    // Update user state and notify parent NOW, after confirmation has been shown.
    // This ensures the user sees the confirmation before any parent re-renders
    // (like price recalculation in checkout flow) occur.
    if (purchasedUserData) {
      setUser(purchasedUserData)
      onPurchaseSuccess?.(purchasedUserData, paymentMethod)
    }

    handleClose({ onSuccess: true })
  }

  const handleCreateMembership = () => {
    const { dockwa_plus_external_id: dockwaPlusExternalId } =
      dockwaPlusProductInformation

    tracker.trackEvent("dockwa_plus:purchase_pressed", {
      dockwa_plus_external_id: dockwaPlusExternalId,
    })

    const membershipData = {
      paymentMethodId: paymentMethod,
      address: address,
      dockwaPlusVersion: dockwaPlusProductInformation.dockwa_plus_version,
    }

    if (couponCode.trim()) {
      membershipData.couponId = couponCode.trim()
    }

    createMembership(membershipData)
  }

  const renderMembershipInformation = () => (
    <div className="pb-4">
      <div className="flex space-x-4">
        <DockwaPlusMembershipCardSmall />
        <div className="flex flex-col space-y-2">
          <span className="font-bold">Dockwa+ Membership</span>
          <span className="text-gray-600">
            Renews on{" "}
            {dockwaPlusProductInformation &&
              format(
                new Date(dockwaPlusProductInformation.renewal_date),
                "MMM d, yyyy"
              )}
          </span>
          <span className="text-gray-600">Discounts apply automatically</span>
        </div>
      </div>
    </div>
  )

  const renderPaymentMethodForm = () => {
    return (
      <div className="flex flex-col space-y-4">
        <div className="mt-2">
          <SelectPaymentMethod
            acceptedPaymentMethods={ACCEPTED_PAYMENT_METHODS}
            paymentMethods={creditCards}
            setPaymentMethods={setPaymentMethods}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            trackingProps={{
              paymentMethodSelected: {
                eventName: "dockwa_plus_signup:payment_method_selected",
                eventProps: {},
              },
              paymentMethodAdded: {
                eventName: "dockwa_plus_signup:payment_method_added",
                eventProps: {},
              },
            }}
          />
        </div>
        {paymentMethod && paymentMethod !== "add" && (
          <div>
            <Form.Label htmlFor="address">Billing Address</Form.Label>
            <Elements stripe={stripePromise} key={paymentMethod}>
              <AddressElement
                id="address"
                options={{
                  mode: "billing",
                  allowedCountries: ["US", "CA"],
                  fields: {
                    phone: "never",
                  },
                  defaultValues: existingBillingAddress,
                }}
                onChange={(event) => {
                  setAddressComplete(event.complete)
                  if (event.complete) {
                    setAddress({
                      ...event.value.address,
                      name: event.value.name,
                    })
                  }
                }}
              />
            </Elements>
          </div>
        )}
      </div>
    )
  }

  const renderLineItems = () => (
    <div className="mb-4">
      <LineItems
        lineItems={dockwaPlusProductInformation?.line_items}
        loading={productInformationLoading}
        loadingItems={4}
      />
    </div>
  )

  const renderLinks = () => (
    <>
      <div className="mt-6 flex items-center gap-4 text-xs text-gray-600">
        <input
          className="m-0"
          type="checkbox"
          id="termsAndConditionsAccepted"
          name="termsAndConditionsAccepted"
          checked={termsAndConditionsAccepted}
          onChange={() =>
            setTermsAndConditionsAccepted(!termsAndConditionsAccepted)
          }
        />
        <label className="m-0" htmlFor="termsAndConditionsAccepted">
          I agree to{" "}
          <a
            href="https://ahoy.dockwa.com/about-us/terms-of-service"
            className="font-semibold text-blue-600"
            rel="noreferrer noopener"
            target="_blank"
          >
            Dockwa&apos;s Terms and Conditions
          </a>{" "}
          and{" "}
          <a
            href="https://ahoy.dockwa.com/privacy"
            className="font-semibold text-blue-600"
            rel="noreferrer noopener"
            target="_blank"
          >
            Privacy Policy
          </a>{" "}
          and consent to being charged annually, with automatic renewals until I
          cancel in my account settings. I understand that pricing may change,
          and no refunds will be provided for unused subscription periods.
        </label>
      </div>
    </>
  )

  const renderCouponSection = () => (
    <div className="mt-8 text-xs">
      {!showCouponInput ? (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault()
            setShowCouponInput(true)
          }}
          className="cursor-pointer text-gray-800"
          role="button"
        >
          Do you have a coupon code?
        </a>
      ) : (
        <div className="flex flex-col space-y-2">
          <Form.Label htmlFor="couponCode" className="font-medium">
            Coupon Code
          </Form.Label>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <Form.TextField
                id="couponCode"
                type="text"
                className={classNames(
                  "form-input rounded border px-3 py-2 text-sm",
                  {
                    "border-gray-300":
                      !dockwaPlusProductInformation?.coupon_status ||
                      dockwaPlusProductInformation?.coupon_status?.status ===
                        "applied",
                    "border-red-500":
                      dockwaPlusProductInformation?.coupon_status?.status ===
                      "invalid",
                  }
                )}
                value={couponCode}
                onChange={(e) => updateField("couponCode", e.target.value)}
                placeholder="Enter your coupon code"
              />
            </div>
            {dockwaPlusProductInformation?.coupon_status && (
              <div
                className={classNames("text-xs", {
                  "text-teal-600":
                    dockwaPlusProductInformation.coupon_status.status ===
                    "applied",
                  "text-red-600":
                    dockwaPlusProductInformation.coupon_status.status ===
                    "invalid",
                })}
              >
                {dockwaPlusProductInformation.coupon_status.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const disablePurchase =
    !paymentMethod ||
    !termsAndConditionsAccepted ||
    !addressComplete ||
    !dockwaPlusProductInformation

  const renderConfirmationScreen = () => (
    <div className="flex flex-col items-center gap-6 py-4">
      <h2 className="m-0 text-2xl font-bold">Membership Confirmed</h2>
      <Stanwood />
      <div className="text-center text-gray-600">
        Welcome aboard! You are now an official Dockwa+ member.
        <br />
        We&apos;ve emailed you a confirmation to {purchasedUserData?.email}
      </div>
    </div>
  )

  if (showConfirmation) {
    return (
      <Modal isOpen={isOpen} onClose={handleContinue} size={size}>
        <Modal.Body>{renderConfirmationScreen()}</Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleContinue}
            >
              Continue
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={size}>
      <Modal.Header title="Sign up for Dockwa+" />
      <Modal.Body>
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left column: Payment & Address */}
          <div className="flex-1">{renderPaymentMethodForm()}</div>
          {/* Right column: Summary & Terms */}
          <div className="flex-1 lg:border-l lg:pl-6">
            {renderMembershipInformation()}
            <div className="mt-4" />
            {renderLineItems()}
            {renderLinks()}
            {renderCouponSection()}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        onClose={handleClose}
        confirmBtnText={submitButtonText()}
        confirmBtnVariant="primary"
        confirmBtnLoading={creatingMembership || productInformationLoading}
        onSubmit={handleCreateMembership}
        disabled={disablePurchase}
        cancelBtnText="Cancel"
        disableCancelBtn={creatingMembership}
      />
    </Modal>
  )
}

DockwaPlusMembershipSignupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array,
  setPaymentMethods: PropTypes.func,
  onPurchaseSuccess: PropTypes.func,
  size: PropTypes.oneOf([
    "small",
    "mediumFixed",
    "large",
    "extraLarge",
    "default",
    "mediumResponsive",
  ]),
}

export default DockwaPlusMembershipSignupModal
